import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { IconClose } from "../shared";

interface NavItem {
  id: string;
  label: string;
  path: string;
  icon: React.ReactNode;
  children?: { path: string; label: string }[];
}

interface SidebarProps {
  isMobile: boolean;
  isOpen: boolean;
  onClose: () => void;
}

const NAV_ITEMS: NavItem[] = [
  { id: "dashboard", label: "Dashboard", path: "/", icon: <DashboardIcon /> },
  { id: "calendar", label: "Calendar", path: "/calendar", icon: <CalendarIcon /> },
  { id: "clients", label: "Clients", path: "/clients", icon: <ContactsIcon /> },
  { id: "services", label: "Services", path: "/services", icon: <ServicesIcon /> },
  { 
    id: "scheduling", 
    label: "Scheduling", 
    path: "/calendars",
    icon: <ScheduleIcon />,
    children: [
      { path: "/calendars", label: "Staff Calendars" },
      { path: "/availability", label: "Availability" },
    ]
  },
  { 
    id: "settings", 
    label: "Settings", 
    path: "/resources",
    icon: <SettingsIcon />,
    children: [
      { path: "/resources", label: "Resources" },
      { path: "/labels", label: "Labels" },
      { path: "/saved-locations", label: "Locations" },
      { path: "/appointment-addons", label: "Add-ons" },
    ]
  },
  { 
    id: "programs", 
    label: "Programs", 
    path: "/classes",
    icon: <ProgramsIcon />,
    children: [
      { path: "/classes", label: "Classes" },
      { path: "/forms", label: "Forms" },
    ]
  },
];

export function Sidebar({ isMobile, isOpen, onClose }: SidebarProps) {
  const location = useLocation();

  useEffect(() => {
    if (!isMobile || !isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isMobile, isOpen, onClose]);

  if (isMobile && !isOpen) return null;

  const isActive = (item: NavItem) => {
    if (item.children) {
      return item.children.some(child => location.pathname.startsWith(child.path));
    }
    if (item.path === "/") {
      return location.pathname === "/";
    }
    // Calendar is also active when on appointment routes
    if (item.path === "/calendar") {
      return location.pathname.startsWith("/calendar") || location.pathname.startsWith("/appointments");
    }
    return location.pathname.startsWith(item.path);
  };

  const sidebarContent = (
    <div className={`h-full bg-sidebar-bg flex flex-col shrink-0 ${isMobile ? "w-full" : "w-60"}`}>
      {/* Logo */}
      <div className="p-5 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-sidebar-active flex items-center justify-center text-lg">
            🐕
          </div>
          <span className="text-white font-bold text-lg">Wooflit</span>
        </div>
        {isMobile && (
          <button
            onClick={onClose}
            className="bg-transparent border-none text-white cursor-pointer p-1 hover:opacity-80 min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Close menu"
          >
            <IconClose size={24} />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="p-3 flex-1 overflow-y-auto" aria-label="Main navigation">
        {NAV_ITEMS.map((item) => {
          const active = isActive(item);
          
          if (item.children) {
            return (
              <div key={item.id} className="mb-1">
                <div className="px-4 py-2 text-white/50 text-xs uppercase tracking-wide">
                  {item.label}
                </div>
                {item.children.map((child) => {
                  const childActive = location.pathname.startsWith(child.path);
                  return (
                    <Link
                      key={child.path}
                      to={child.path}
                      onClick={() => isMobile && onClose()}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 mb-0.5 rounded-lg transition-colors min-h-[40px] text-white text-sm no-underline ${
                        childActive
                          ? "bg-sidebar-active"
                          : "bg-transparent hover:bg-sidebar-hover"
                      }`}
                    >
                      <span className={`${childActive ? "font-semibold" : "font-normal"}`}>
                        {child.label}
                      </span>
                    </Link>
                  );
                })}
              </div>
            );
          }

          return (
            <Link
              key={item.id}
              to={item.path}
              onClick={() => isMobile && onClose()}
              className={`w-full flex items-center gap-3 px-4 py-3 mb-1 rounded-lg transition-colors min-h-[44px] text-white no-underline ${
                active ? "bg-sidebar-active" : "bg-transparent hover:bg-sidebar-hover"
              }`}
            >
              <span className="text-lg text-white/80">{item.icon}</span>
              <span className={`text-sm ${active ? "font-semibold" : "font-normal"}`}>
                {item.label}
              </span>
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="px-5 py-4 border-t border-white/10">
        <div className="text-white/50 text-xs">v0.1.0</div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div className="fixed inset-0 z-[1000] flex">
        <div
          className="absolute inset-0 bg-black/50"
          onClick={onClose}
          aria-hidden="true"
        />
        <div className="relative z-10 w-72">{sidebarContent}</div>
      </div>
    );
  }

  return sidebarContent;
}

function DashboardIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function ContactsIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function ServicesIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function ScheduleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function ProgramsIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  );
}
